<template>
  <KekkleBottomSheet
    v-model:open="isOpen"
    shadow="blue"
    padding="normal"
    initial-height="full"
  >
    <div class="dark:text-slate-200">
      <!-- Header -->
      <div class="mb-6 flex items-center justify-between">
        <h2 class="flex-1 text-2xl font-bold">{{ $t("pages.event.edit") }}</h2>
        <DefaultButton
          class="mx-0"
          :disabled="!validated"
          :loading="status.updating || status.deleting"
          @click="update"
        >
          {{ $t("global.inputs.labels.update") }}
        </DefaultButton>
      </div>

      <!-- Form -->
      <div class="mb-4 w-full">
        <text-input
          v-model="name"
          :placeholder="$t('global.inputs.placeholders.event')"
          :label="$t('global.inputs.labels.name_event')"
          :has-error="validationErrors.name"
          :error-message="$t('global.inputs.errors.name_event')"
        />
      </div>

      <div class="mb-4 w-full">
        <text-area-input
          v-model="description"
          :has-error="validationErrors.description"
          :error-message="$t('global.inputs.errors.description_event')"
          :label="$t('global.inputs.labels.description_event')"
          :placeholder="$t('global.inputs.placeholders.event_description')"
        />
      </div>

      <div class="mb-4 w-full">
        <p class="text-sm font-medium italic dark:text-slate-200">
          {{ $t("global.inputs.labels.date_time") }}*
        </p>
        <p
          v-show="validationErrors.timestamp"
          class="mt-1 text-sm text-red-600"
        >
          {{ $t("global.inputs.errors.date_time") }}
        </p>
        <div class="flex w-full flex-col items-center">
          <date-picker
            v-model.range="range"
            range
            is24hr
            mode="dateTime"
            class="mx-auto border-2 border-black-dark shadow-blue-hard-sm dark:bg-kekkle-dark-light"
            show-iso-weeknumbers
            expanded
            :is-dark="darkModeEnabled"
          />
        </div>
      </div>

      <!-- Delete Section -->
      <div v-if="!showDeleteConfirmation" class="mb-4 w-full">
        <default-button
          color="red"
          :disabled="status.updating"
          :loading="status.deleting"
          @click="showDeleteConfirmation = true"
        >
          <p>{{ $t("global.inputs.labels.delete_event") }}</p>
        </default-button>
      </div>

      <!-- Inline Delete Confirmation -->
      <inline-delete-confirmation
        v-else
        color="red"
        :confirm-text="$t('pages.event.confirm_delete_text')"
        :cancel-text="$t('pages.event.confirm_close')"
        :delete-text="$t('pages.event.confirm_delete')"
        :loading="status.deleting"
        @cancel="showDeleteConfirmation = false"
        @confirm="deleteEvent"
      />
    </div>
  </KekkleBottomSheet>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { DatePicker } from "v-calendar";
import {
  setSuccessToast,
  setWarningToast,
} from "@/helpers/notifications/toast";
import TextInput from "@/components/Inputs/TextInput.vue";
import TextAreaInput from "@/components/Inputs/TextAreaInput.vue";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";
import KekkleBottomSheet from "@/components/ui/KekkleBottomSheet.vue";
import InlineDeleteConfirmation from "@/components/InlineDeleteConfirmation.vue";
import { useDarkModeStore } from "@/stores/modules/darkmode/darkmode";
import { useEventsStore } from "@/stores/modules/events/events";

type EventData = {
  _id: string;
  name: string;
  description: string;
  begin_timestamp: Date;
  end_timestamp: Date;
};

type Props = {
  eventData: EventData | null;
};

const props = defineProps<Props>();
const { t } = useI18n();
const darkModeStore = useDarkModeStore();
const eventsStore = useEventsStore();

const isOpen = defineModel<boolean>("open", { required: true });

// Form state
const status = ref({
  updating: false,
  deleting: false,
});
const name = ref<string | undefined>(undefined);
const description = ref<string | undefined>(undefined);
const range = ref<any>({});
const showDeleteConfirmation = ref(false);
const eventId = ref<string | null>(null);

// Computed properties
const darkModeEnabled = computed(() => darkModeStore.darkModeEnabled);

const nameValid = computed(() => !!(name.value && name.value.length > 0));

const descriptionValid = computed(
  () => !!(description.value && description.value.length > 0),
);

const timestampValid = computed(
  () =>
    !!(
      range.value.start &&
      range.value.end &&
      range.value.start.getTime() < range.value.end.getTime()
    ),
);

const validationErrors = computed(() => ({
  name: !nameValid.value,
  description: !descriptionValid.value,
  timestamp: !timestampValid.value,
}));

const validated = computed(() => {
  return !Object.values(validationErrors.value).some((value) => value === true);
});

// Methods
function loadEventData(data: EventData) {
  if (!data) return;

  eventId.value = data._id;
  name.value = data.name;
  description.value = data.description;
  range.value = {
    start: data.begin_timestamp,
    end: data.end_timestamp,
  };
}

async function update() {
  if (!validated.value) {
    setWarningToast({
      message: t("global.toast.check_all_fields"),
    });
    return;
  }

  try {
    status.value.updating = true;
    await eventsStore.updateEvent(eventId.value!, {
      name: name.value!,
      description: description.value!,
      begin_timestamp: range.value.start!,
      end_timestamp: range.value.end!,
    });

    setSuccessToast({
      message: t("global.toast.event_saved"),
      emoji: "📅",
    });
    isOpen.value = false;
  } catch (e) {
    console.error("Error updating event:", e);
  } finally {
    status.value.updating = false;
  }
}

async function deleteEvent() {
  try {
    status.value.deleting = true;

    await eventsStore.deleteEvent(eventId.value!);

    setSuccessToast({
      message: t("global.toast.deleted_successfully"),
      emoji: "🗑️",
    });
    isOpen.value = false;
  } catch (e) {
    console.error("Error deleting event:", e);
  } finally {
    status.value.deleting = false;
  }
}

// Watchers
watch(isOpen, (newValue) => {
  if (!newValue) {
    // Reset delete confirmation when closing
    showDeleteConfirmation.value = false;
  } else if (newValue && props.eventData) {
    // Load data when opening
    loadEventData(props.eventData);
  }
});

watch(
  () => props.eventData,
  (newData) => {
    if (newData && isOpen.value) {
      loadEventData(newData);
    }
  },
  { immediate: true },
);
</script>
